import * as Dialog from '@radix-ui/react-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { FxButton, FxIcon, FxTextField } from '@/components/fx'
import { templatesApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import type { Template } from '@/types/domain'
import type { Folder } from '@/lib/api/folders'

/**
 * The card and row kebab: Umbenennen, In Projekt verschieben, Archivieren —
 * the three the Flutter overview offers, all three backed by endpoints the
 * client already has.
 *
 * Feedback stays inside the dialog instead of going to a toast: the cockpit
 * build mounts no `Toaster`, so a toast here would be a failure message
 * nobody sees. A failed save keeps the dialog open with the reason on it,
 * which is where the person still is.
 */

type OpenDialog = null | 'rename' | 'move' | 'archive'

export function TemplateActions({
  template,
  folders,
  open,
  onOpenChange,
}: {
  template: Template
  folders: Folder[]
  /** Controlled by the card, whose kebab is its own button. */
  open: OpenDialog
  onOpenChange: (next: OpenDialog) => void
}) {
  return (
    <>
      <RenameDialog
        template={template}
        open={open === 'rename'}
        onClose={() => onOpenChange(null)}
      />
      <MoveDialog
        template={template}
        folders={folders}
        open={open === 'move'}
        onClose={() => onOpenChange(null)}
      />
      <ArchiveDialog
        template={template}
        open={open === 'archive'}
        onClose={() => onOpenChange(null)}
      />
    </>
  )
}

/** The kebab itself, so a card and a list row open the same menu. */
export function TemplateMenuButton({
  name,
  onSelect,
  className,
}: {
  name: string
  onSelect: (action: Exclude<OpenDialog, null>) => void
  className?: string
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`Aktionen für ${name}`}
          className={cn('relative z-10 flex flex-none rounded-sm p-1 text-text', className)}
        >
          <FxIcon name="more_vert" size={24} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          collisionPadding={8}
          className="z-50 min-w-[240px] rounded-xl bg-surface p-2 text-body text-text shadow-overlay"
        >
          <MenuItem icon="edit" onSelect={() => onSelect('rename')}>
            Umbenennen
          </MenuItem>
          <MenuItem icon="drive_file_move" onSelect={() => onSelect('move')}>
            In Projekt verschieben
          </MenuItem>
          <MenuItem icon="archive" onSelect={() => onSelect('archive')}>
            Archivieren
          </MenuItem>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

function MenuItem({
  icon,
  onSelect,
  children,
}: {
  icon: string
  onSelect: () => void
  children: React.ReactNode
}) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className="flex cursor-default items-center gap-2 rounded-sm px-3 py-2 outline-none data-[highlighted]:bg-primary-subtle data-[highlighted]:text-primary-text"
    >
      <FxIcon name={icon} size={20} />
      {children}
    </DropdownMenu.Item>
  )
}

/**
 * One dialog skin for the three, in the design's overlay shape (32px radius,
 * `shadow-overlay`, a dimmed page). Radix carries the focus trap, the
 * Escape key and the labelled title.
 */
function Shell({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-[#0000008c]" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[min(460px,calc(100vw-40px))] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-surface p-5 shadow-overlay xs:p-8">
          <Dialog.Title className="mb-4 text-dialog-title text-text">{title}</Dialog.Title>
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function useRefreshTemplates() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ['templates'] })
}

function RenameDialog({
  template,
  open,
  onClose,
}: {
  template: Template
  open: boolean
  onClose: () => void
}) {
  const [title, setTitle] = useState(template.title)
  const refresh = useRefreshTemplates()
  const save = useMutation({
    mutationFn: (next: string) => templatesApi.rename(template.id, next),
    onSuccess: async () => {
      await refresh()
      onClose()
    },
  })

  const trimmed = title.trim()
  return (
    <Shell
      open={open}
      onClose={onClose}
      title="Musterformular umbenennen"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (!trimmed || trimmed === template.title) {
            onClose()
            return
          }
          save.mutate(trimmed)
        }}
      >
        <FxTextField
          label="Name"
          autoFocus
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          error={save.isError ? 'Der Name konnte nicht gespeichert werden.' : undefined}
        />
        <div className="mt-8 flex justify-end gap-4">
          <FxButton variant="outlined" onClick={onClose}>
            Abbrechen
          </FxButton>
          <FxButton type="submit" disabled={!trimmed || save.isPending}>
            Speichern
          </FxButton>
        </div>
      </form>
    </Shell>
  )
}

function MoveDialog({
  template,
  folders,
  open,
  onClose,
}: {
  template: Template
  folders: Folder[]
  open: boolean
  onClose: () => void
}) {
  const refresh = useRefreshTemplates()
  const move = useMutation({
    mutationFn: (folderId: string) => templatesApi.setFolder(template.id, folderId),
    onSuccess: async () => {
      await refresh()
      onClose()
    },
  })

  return (
    <Shell open={open} onClose={onClose} title="In Projekt verschieben">
      {folders.length === 0 ? (
        <p className="text-body text-text-muted">
          Es gibt noch keine Projekte. Legen Sie eines unter „Projekte“ an.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {folders.map((folder) => (
            <li key={folder.id}>
              <button
                type="button"
                className={cn(
                  'flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-body',
                  folder.id === template.folder
                    ? 'bg-primary-subtle text-primary-text'
                    : 'hover:bg-ground',
                )}
                disabled={move.isPending}
                onClick={() => move.mutate(folder.id)}
              >
                <FxIcon name="folder" size={20} filled />
                <span className="min-w-0 flex-1 truncate">{folder.name}</span>
                {folder.id === template.folder && (
                  <span className="text-small">aktuelles Projekt</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {move.isError && (
        <p className="mt-4 text-small text-danger-text">
          Das Verschieben hat nicht geklappt. Bitte noch einmal versuchen.
        </p>
      )}
      <div className="mt-8 flex justify-end">
        <FxButton variant="outlined" onClick={onClose}>
          Abbrechen
        </FxButton>
      </div>
    </Shell>
  )
}

function ArchiveDialog({
  template,
  open,
  onClose,
}: {
  template: Template
  open: boolean
  onClose: () => void
}) {
  const refresh = useRefreshTemplates()
  const archive = useMutation({
    mutationFn: () => templatesApi.archive(template.id),
    onSuccess: async () => {
      await refresh()
      onClose()
    },
  })

  return (
    <Shell open={open} onClose={onClose} title="Musterformular archivieren">
      {/* Archiving is reversible — `POST /v1/templates/:id/restore` — and the
          copy says so, because a confirm that sounds final makes people keep
          forms they do not want. */}
      <p className="text-body text-text">
        „{template.title}“ wird ins Archiv gelegt. Die Dokumente bleiben erhalten, und Sie können
        das Formular jederzeit wiederherstellen.
      </p>
      {archive.isError && (
        <p className="mt-4 text-small text-danger-text">
          Das Archivieren hat nicht geklappt. Bitte noch einmal versuchen.
        </p>
      )}
      <div className="mt-8 flex justify-end gap-4">
        <FxButton variant="outlined" onClick={onClose}>
          Abbrechen
        </FxButton>
        <FxButton disabled={archive.isPending} onClick={() => archive.mutate()}>
          Archivieren
        </FxButton>
      </div>
    </Shell>
  )
}

export type { OpenDialog }
